// Calendar / homework (web parity for iOS CalendarView). Shows personal +
// class-inherited entries in an interactive month grid (default) or a grouped
// list. App-blocking is iOS-only — on web a "block" is shown as a schedule.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type CalendarEntry,
  type CalendarEntryKind,
  deleteCalendarEntry,
  listCalendar,
} from "@/api/calendar";
import { Button, Spinner, cx } from "@/components/ui";
import { EntryForm, type EntrySeed, scheduleText } from "./entry-form";
import { CalendarGrid, type GridSeed } from "./calendar-grid";
import { SubscribeButton } from "./subscribe-button";

type FormState = { mode: "create"; seed?: EntrySeed } | { mode: "edit"; entry: CalendarEntry } | null;

export function CalendarPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const [form, setForm] = useState<FormState>(null);
  const entriesQuery = useQuery({ queryKey: ["calendar"], queryFn: listCalendar });
  const entries = entriesQuery.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["calendar"] });
  const del = useMutation({
    mutationFn: (id: string) => deleteCalendarEntry(id),
    onSuccess: invalidate,
  });

  const groups: { key: CalendarEntryKind; label: string }[] = [
    { key: "homework", label: t("Homework") },
    { key: "block", label: t("Blocks") },
    { key: "task", label: t("Tasks") },
  ];

  const openCreate = (seed?: GridSeed) => setForm({ mode: "create", seed });
  const openEdit = (entry: CalendarEntry) => setForm({ mode: "edit", entry });
  const closeForm = () => setForm(null);

  return (
    <div className="mx-auto max-w-3xl animate-fade-up">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-3xl font-bold tracking-tight">{t("Calendar")}</h1>
        <div className="flex items-center gap-2">
          <SubscribeButton />
          <Button size="sm" variant="primary" onClick={() => openCreate()}>
            {t("Add entry")}
          </Button>
        </div>
      </div>
      <p className="mt-1 text-sm text-ink-faint">
        {t("Homework, tasks, and app-blocking windows. App-blocking is enforced on iOS; here it's your schedule.")}
      </p>

      <div className="mt-3 inline-flex rounded-xl bg-black/5 p-1">
        {(["calendar", "list"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={cx(
              "rounded-lg px-3 py-1 text-sm font-semibold capitalize transition",
              view === v ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
            )}
          >
            {v === "calendar" ? t("Calendar") : t("List")}
          </button>
        ))}
      </div>

      {form && (
        <div className="mt-4">
          <EntryForm
            initial={form.mode === "edit" ? form.entry : undefined}
            seed={form.mode === "create" ? form.seed : undefined}
            // Inherited class entries are read-only from the personal calendar.
            readOnly={form.mode === "edit" && form.entry.source === "class"}
            onDone={() => { closeForm(); invalidate(); }}
            onCancel={closeForm}
          />
        </div>
      )}

      {entriesQuery.isLoading && (
        <div className="flex justify-center py-16 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {!entriesQuery.isLoading && view === "calendar" && (
        <div className="mt-4">
          <CalendarGrid
            entries={entries}
            showClanBadges
            canEdit
            onCreate={openCreate}
            onEdit={openEdit}
          />
        </div>
      )}

      {!entriesQuery.isLoading && view === "list" && (
        <>
          {entries.length === 0 && !form && (
            <p className="mt-10 text-center text-ink-faint">{t("Nothing scheduled yet.")}</p>
          )}
          <div className="mt-6 space-y-6">
            {groups.map(({ key, label }) => {
              const items = entries.filter((e) => e.entry_kind === key);
              if (items.length === 0) return null;
              return (
                <div key={key}>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">{label}</h2>
                  <div className="mt-2 space-y-2">
                    {items.map((e) => (
                      <div key={e.id} className="card flex items-center gap-3 p-3.5">
                        <button className="min-w-0 flex-1 text-left" onClick={() => openEdit(e)}>
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium text-ink">{e.title}</span>
                            {e.source === "class" && e.clan_name && (
                              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700">
                                {e.clan_name}
                              </span>
                            )}
                          </div>
                          {scheduleText(e) && <p className="truncate text-xs text-ink-faint">{scheduleText(e)}</p>}
                          {e.question_set_titles.length > 0 && (
                            <p className="truncate text-xs text-ink-faint">{e.question_set_titles.join(", ")}</p>
                          )}
                        </button>
                        {e.entry_kind === "homework" && e.question_set_ids.length > 0 && (
                          <Link
                            to={`/app/library/${e.question_set_ids[0]}/play`}
                            className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            {t("Practice")}
                          </Link>
                        )}
                        {e.source === "self" && (
                          <button
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => {
                              if (window.confirm(t("Delete this entry?"))) del.mutate(e.id);
                            }}
                          >
                            {t("Delete")}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
